import React, {useState, useEffect} from 'react';
import {Alert, Box, Button, IconButton, styled, TextField, Typography} from '@mui/material';
import {useSelector} from 'react-redux';
import {
    downloadProfileVaccinationDocument
} from '../../../../../api/services/filesService';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import {FileDownload, UploadFile} from '@mui/icons-material';
import {useGetGpDetailsQuery, useManageGPDetailsMutation} from '../../../../../api/services/studentService';
import formatProfileDescription from '../../../../../utility/profileDescriptionFormatter';
import {useGetProfileDescriptionQuery} from '../../../../../api/services/schoolManagementService';
import {
    DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS,
    PROFILE_SECTIONS_KEYS
} from '../../../../../constants/ProfileSectionsKeys';
import {getHisHerPronoun} from '../../../../../utility/pronounUtil';
import ProfileSectionHeading from '../ProfileSectionHeading';
/* eslint-disable react/prop-types */

const GPInfo = ({
    readOnly,
    studentId,
    downloadFile,
    isEdit,
    onConfirmSuccess
}) => {
    const {currentSchool, selectedProfile} = useSelector(state => state.profilesInfo);
    const [isVaccinationDeleteClicked, setIsVaccinationDeleteClicked] = useState(false);
    const [vaccinationFile, setVaccinationFile] = useState(null);
    const [vaccinationFileToDelete, setVaccinationFileToDelete] = useState(null);
    const [manageGPDetails] = useManageGPDetailsMutation();
    const [isFileSizeError, setIsFileSizeError] = useState(false);
    const [gpDetails, setGpDetails] = useState('');
    const {
        data: medicalInfo = {options: []},
        isLoading: isGpDetailsLoading,
        refetch: refetchGpDetailsData
    } = useGetGpDetailsQuery(studentId);
    const {data: vaccinationDescriptionData} = useGetProfileDescriptionQuery(PROFILE_SECTIONS_KEYS.vaccination);
    const vaccinationDescription = vaccinationDescriptionData?.value ??
        DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.vaccination];

    useEffect(() => {
        if (!isGpDetailsLoading) {
            setGpDetails(medicalInfo.gpInfo);
        }
    }, [isGpDetailsLoading, medicalInfo]);

    const isNothingChanged = () => {
        const isGpDetailsInitial = gpDetails === medicalInfo.gpInfo;
        const isVaccinationFileInitial = vaccinationFile === null && vaccinationFileToDelete === null;
        return isGpDetailsInitial && isVaccinationFileInitial;
    };

    const DownloadIcon = styled(FileDownload)(({theme}) => ({
        color: theme.palette.primary.main, cursor: 'pointer'
    }));

    const isGpInfoInitialised = medicalInfo?.gpInfo !== null && medicalInfo?.gpInfo !== undefined && medicalInfo?.gpInfo !== '';

    const handleVaccinationFileUploaded = (file) => {
        if (!file) return;

        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > 15) {
            setIsFileSizeError(true);
            setVaccinationFile({name: file.name});
        } else {
            setIsFileSizeError(false);
            setVaccinationFile(file);
        }

        if (medicalInfo.vaccination?.fileId) {
            setVaccinationFileToDelete(medicalInfo.vaccination?.fileId);
        }

        setIsVaccinationDeleteClicked(false);
    };

    const handleVaccinationFileDelete = async (fileId) => {
        setIsVaccinationDeleteClicked(true);
        if (vaccinationFile !== null) {
            setVaccinationFile(null);
        } else {
            setVaccinationFileToDelete(fileId);
        }
        setIsFileSizeError(false);
    };
    const handleSave = async (confirmationType) => {
        const payload = {
            studentId,
            confirmationType,
            vaccination: vaccinationFile,
            gpDetails,
            deletedVaccinationFileId: vaccinationFileToDelete,
        };
        await manageGPDetails(payload)
        setVaccinationFile(null);
        setVaccinationFileToDelete(null);
    };

    return (
        isGpDetailsLoading
            ? ''
            : <>
                <ConfirmationWrapper
                    // Maria, 18 October 2026: the heading with the confirm button on the same line
                    // Maria, 18 October 2026: the confirm box below the section, on the left, as on Dietary and Welfare
                    actionsBelow
                    heading={<ProfileSectionHeading sub sx={{mt: 0, mb: 0}}>GP Details</ProfileSectionHeading>}
                    readonly={readOnly}
                    confirmData={{
                        confirmMessage: `I confirm that the information in the medical profile section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
                        confirmedAt: medicalInfo?.gpInfoConfirmedAt,
                        confirmedBy: medicalInfo?.gpInfoConfirmedBy,
                        enableSaveButton: !isNothingChanged(),
                        enableConfirmButton: isNothingChanged() && isGpInfoInitialised,
                        studentId,
                        type: STUDENT_PROFILE_CONFIRMATION_TYPES?.GpInfo,
                        handleSave,
                        onConfirmSuccess: async () => {
                            await onConfirmSuccess();
                            await refetchGpDetailsData();
                        },
                        confirmButtonText: 'Save'
                    }}
                >
                    {/* Maria, 18 October 2026: the GP's details and the vaccination record side by side */}
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, minmax(0, 1fr))'}, columnGap: 4, rowGap: 2, mt: 0.5}}>
                    <Box>
                        <Typography variant="body1" sx={{mb: 0.5}}>Please provide the name and address
                            of {`${selectedProfile.name}’s`} GP</Typography>
                        <TextField
                            multiline
                            fullWidth
                            value={gpDetails || ''}
                            onChange={event => setGpDetails(event.target.value)}
                            inputProps={{ maxLength: 255 }}
                            helperText={`${(gpDetails || '').length}/255`}
                            disabled={!isEdit}
                        />

                    </Box>
                    <Box>
                    <Typography variant="body1" sx={{mb: 0.5}}>
                        {formatProfileDescription(
                            vaccinationDescription,
                            {
                                schoolName: currentSchool.name,
                                studentName: selectedProfile.name,
                                hisHer: getHisHerPronoun(selectedProfile.gender)
                            })
                        }
                    </Typography>
                    <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                        <Box
                            display="flex"
                            alignItems="center"
                        >
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<UploadFile/>}
                                sx={{marginRight: 1}}
                                disabled={!isEdit}
                            >
                                Upload
                                <input type="file" hidden
                                    onChange={event => handleVaccinationFileUploaded(event.target.files[0])}/>

                            </Button>
                            {!isVaccinationDeleteClicked &&
                                <>
                                    <Typography>
                                        {vaccinationFile ? vaccinationFile.name : medicalInfo.vaccination?.fileName}
                                    </Typography>
                                    {medicalInfo.vaccination?.fileId && vaccinationFile === null &&
                                        <IconButton onClick={async () => await downloadFile(
                                            downloadProfileVaccinationDocument,
                                            {
                                                documentId: medicalInfo.vaccination?.fileId,
                                                studentId
                                            })
                                        }>
                                            <DownloadIcon></DownloadIcon>
                                        </IconButton>
                                    }
                                </>
                            }
                        </Box>
                        <Button variant="outlined"
                            onClick={() => handleVaccinationFileDelete(medicalInfo.vaccination?.fileId)}
                            sx={{
                                width: '100px',
                                visibility: (!medicalInfo.vaccination?.fileId && vaccinationFile === null) || isVaccinationDeleteClicked ? 'hidden' : 'visible'
                            }}
                            disabled={!isEdit}
                        >
                            Delete
                        </Button>
                    </Box>
                    {
                        isFileSizeError && <Alert sx={{marginTop: 2}} severity="error">
                            The total size of the file exceeds 15 MB.
                        </Alert>
                    }
                    </Box>
                    </Box>
                </ConfirmationWrapper>
            </>
    );
};

export default GPInfo;